<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import { useI18n } from 'vue-i18n';
import {
  stageColor,
  findListAttribute,
  TYPE_ATTRIBUTE_HINT,
  STAGE_ATTRIBUTE_HINT,
} from 'dashboard/components-next/Contacts/contactStageColors';
import {
  channelLabel,
  channelDotHex,
} from 'dashboard/components-next/Contacts/contactChannel';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({
  x: null,
  y: null,
});

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

const isInboxNameVisible = computed(() => !activeInbox.value);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

// CrmB row extras: the contact's Tipo / Estado chips, the channel and the
// agent, plus a left bar tinted by the stage (teal when the row is active).
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const typeAttribute = computed(() =>
  findListAttribute(
    contactAttributes.value,
    'tipo_contacto',
    TYPE_ATTRIBUTE_HINT
  )
);
const stageAttribute = computed(() =>
  findListAttribute(
    contactAttributes.value,
    'estado_contacto',
    STAGE_ATTRIBUTE_HINT
  )
);
const attributeValue = attribute =>
  attribute
    ? currentContact.value?.custom_attributes?.[attribute.attributeKey] || ''
    : '';
const contactType = computed(() => attributeValue(typeAttribute.value));
const contactStage = computed(() => attributeValue(stageAttribute.value));
const chipStyle = value => {
  const color = stageColor(value);
  return { backgroundColor: color.bg, color: color.text };
};
const chipDotStyle = value => ({ backgroundColor: stageColor(value).dot });
const barStyle = computed(() =>
  isActiveChat.value
    ? { backgroundColor: '#24707f', opacity: 1 }
    : { backgroundColor: stageColor(contactStage.value).dot, opacity: 0.55 }
);
const channelType = computed(() => chatMetadata.value.channel);
const channelName = computed(() => channelLabel(channelType.value));
const channelDot = computed(() => channelDotHex(channelType.value));
const agentName = computed(
  () => assignee.value.name || t('CONTACTS_LAYOUT.DETAILS.SUMMARY.UNASSIGNED')
);

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
  closeContextMenu();
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <!-- CrmB row: stage bar · avatar · name / unread / time · snippet ·
       tipo · estado · channel · agent. -->
  <div
    class="relative flex items-start gap-[11px] w-auto max-w-full border-b border-n-weak cursor-pointer conversation group transition-colors"
    :class="{
      'active animate-card-select bg-n-brand/[0.06]': isActiveChat,
      'hover:bg-n-alpha-1': !isActiveChat,
      'bg-n-slate-2 dark:bg-n-slate-3': selected,
      'px-2 py-2.5': compact,
      'px-4 py-[11px]': !compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div class="absolute top-0 bottom-0 left-0 w-[3px]" :style="barStyle" />
    <div
      class="relative flex-shrink-0"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="34"
        :status="currentContact.availability_status"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-xl cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>

    <div class="flex flex-col flex-1 min-w-0 gap-[3px]">
      <div class="flex items-baseline gap-2 min-w-0">
        <h4
          class="conversation--user m-0 text-[13.5px] truncate text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <span
          v-if="hasUnread"
          class="inline-flex items-center justify-center flex-shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full text-[10.5px] font-bold text-white bg-n-brand"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
        <PriorityMark :priority="chat.priority" class="flex-shrink-0" />
        <span class="flex-shrink-0 ml-auto text-[11px] text-n-slate-9">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
          />
        </span>
      </div>

      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 text-[12.5px] leading-[1.4] min-w-0 truncate"
        :class="hasUnread ? 'text-n-slate-11' : 'text-n-slate-10'"
      />
      <p
        v-else
        key="no-messages"
        class="my-0 text-[12.5px] leading-[1.4] min-w-0 truncate text-n-slate-10"
      >
        {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
      </p>

      <div
        class="flex items-center gap-1.5 mt-0.5 min-w-0 text-[11px] text-n-slate-10"
      >
        <span
          v-if="contactType"
          class="inline-flex items-center gap-[5px] h-[18px] px-[7px] rounded text-[10.5px] font-semibold flex-shrink-0"
          :style="chipStyle(contactType)"
        >
          <span
            class="size-1.5 rounded-full"
            :style="chipDotStyle(contactType)"
          />
          {{ contactType }}
        </span>
        <span
          v-if="contactStage"
          class="inline-flex items-center h-[18px] px-[7px] rounded text-[10.5px] font-semibold flex-shrink-0"
          :style="chipStyle(contactStage)"
        >
          {{ contactStage }}
        </span>
        <span
          class="size-1.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: channelDot }"
        />
        <span v-if="showInboxName" class="truncate max-w-[7rem]">{{
          inbox.name
        }}</span>
        <span v-else class="flex-shrink-0">{{ channelName }}</span>
        <span class="text-n-slate-8">·</span>
        <span class="truncate">{{ agentName }}</span>
      </div>

      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        hide-synced-labels
        class="mt-1 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
